import {Injectable} from '@angular/core';
import {checkResponse} from '@app/shared/utils/app-utils';
import {
  ConfigurationService,
  GetShowShortNumberResponse,
  GetSwitchboardNumberResponse,
  Response,
  SetSwitchboardNumberResponse
} from '@gt-api';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

export enum NameValueListType {
  COMPANY_BLACKLIST_OUTGOING,
  COMPANY_WHITELIST_OUTGOING,
  BLACKLIST_OUTGOING,
  BLACKLIST_INCOMING,
  WHITELIST_OUTGOING,
  ABSENCE_WHITELIST
}

@Injectable({
  providedIn: 'root'
})
export class GtConfigurationService {
  constructor(private configurationService: ConfigurationService) {}

  public getActiveProfile(organization?: string, login?: string): Observable<number> {
    return new Observable(observer =>
      this.configurationService.getActiveProfile1(organization, login).subscribe(
        r => {
          if (r.code !== 200) {
            observer.error(r.message);
          }
          observer.next(r.activeProfile);
          observer.complete();
        },
        e => observer.error(e.message)
      )
    );
  }

  setPreferredLanguage(organization: string, login: string, language: string): Observable<Response> {
    return this.configurationService.setPreferredLanguage1(organization, login, language);
  }

  getCompanySwitchboardNumber(departmentId: number, organization: string, login: string): Observable<GetSwitchboardNumberResponse> {
    return this.configurationService.getCompanySwitchboardNumber1(departmentId, organization, login);
  }

  setCompanySwitchboardNumber(
    departmentId: number,
    switchboardNumber: string,
    organization?: string,
    login?: string
  ): Observable<SetSwitchboardNumberResponse> {
    return this.configurationService.setCompanySwitchboardNumber1(departmentId, switchboardNumber, organization, login);
  }

  getShowShortNumber(organization: string, login: string): Observable<GetShowShortNumberResponse> {
    return this.configurationService.getShowShortNumber1(organization, login);
  }

  setShowShortNumber(showShortNumber: boolean, organization: string, login: string): Observable<Response> {
    return this.configurationService.setShowShortNumber1(showShortNumber, organization, login);
  }

  getDisplayNumber(organization: string, login: string): Observable<string> {
    return this.configurationService.getDisplayNumber1(organization, login).pipe(
      checkResponse(),
      map(r => {
        if (r.displayNumber && r.displayNumber.displayNumber.length) {
          return r.displayNumber.displayNumber;
        } else {
          throw new Error(r.message);
        }
      })
    );
  }

  setDisplayNumber(organization: string, login: string, displayNumber: string): Observable<void> {
    const observable = this.configurationService.setDisplayNumber1(organization, login, {
      displayNumber,
      enabled: true
    });
    return this.newObservableCheckingResponseCode(observable);
  }

  getNameValueList<T>(listType?: NameValueListType, organization?: string, login?: string): Observable<T[]> {
    let field = 'blacklist';
    let observable: Observable<Response>;
    switch (listType) {
      case NameValueListType.COMPANY_WHITELIST_OUTGOING:
        observable = this.configurationService.getCompanyWhitelist1(false, organization, login);
        field = 'whitelist';
        break;
      case NameValueListType.WHITELIST_OUTGOING:
        observable = this.configurationService.getWhitelist1(false, organization, login);
        field = 'whitelist';
        break;
      case NameValueListType.ABSENCE_WHITELIST:
        observable = this.configurationService.getWhitelist1(true, organization, login);
        field = 'whitelist';
        break;
      case NameValueListType.COMPANY_BLACKLIST_OUTGOING:
        observable = this.configurationService.getCompanyBlacklist1(false, organization, login);
        break;
      case NameValueListType.BLACKLIST_OUTGOING:
        observable = this.configurationService.getBlacklist1(false, organization, login);
        break;
      default:
        observable = this.configurationService.getBlacklist1(true, organization, login);
        break;
    }
    return this.newObservableCheckingResponseCode(observable, field);
  }

  setNameValueList<T>(entries: T[], listType?: NameValueListType, organization?: string, login?: string) {
    let observable: Observable<Response>;
    switch (listType) {
      case NameValueListType.COMPANY_WHITELIST_OUTGOING:
        observable = this.configurationService.setCompanyWhitelist1(false, organization, login, entries);
        break;
      case NameValueListType.WHITELIST_OUTGOING:
        observable = this.configurationService.setWhitelist1(false, organization, login, entries);
        break;
      case NameValueListType.ABSENCE_WHITELIST:
        observable = this.configurationService.setWhitelist1(true, organization, login, entries);
        break;
      case NameValueListType.COMPANY_BLACKLIST_OUTGOING:
        observable = this.configurationService.setCompanyBlacklist1(false, organization, login, entries);
        break;
      case NameValueListType.BLACKLIST_OUTGOING:
        observable = this.configurationService.setBlacklist1(false, organization, login, entries);
        break;
      default:
        observable = this.configurationService.setBlacklist1(true, organization, login, entries);
        break;
    }
    return this.newObservableCheckingResponseCode(observable);
  }

  addNameValueListEntry<T>(entry: T, listType?: NameValueListType, organization?: string, login?: string) {
    let observable: Observable<Response>;
    switch (listType) {
      case NameValueListType.COMPANY_WHITELIST_OUTGOING:
        observable = this.configurationService.addCompanyWhitelistEntry1(false, organization, login, entry);
        break;
      case NameValueListType.WHITELIST_OUTGOING:
        observable = this.configurationService.addWhitelistEntry1(false, organization, login, entry);
        break;
      case NameValueListType.ABSENCE_WHITELIST:
        observable = this.configurationService.addWhitelistEntry1(true, organization, login, entry);
        break;
      case NameValueListType.COMPANY_BLACKLIST_OUTGOING:
        observable = this.configurationService.addCompanyBlacklistEntry1(false, organization, login, entry);
        break;
      case NameValueListType.BLACKLIST_OUTGOING:
        observable = this.configurationService.addBlacklistEntry1(false, organization, login, entry);
        break;
      default:
        observable = this.configurationService.addBlacklistEntry1(true, organization, login, entry);
        break;
    }
    return this.newObservableCheckingResponseCode(observable);
  }

  private newObservableCheckingResponseCode(obs: Observable<Response>, returnField?: string): Observable<any> {
    return new Observable(observer =>
      obs.subscribe(
        r => {
          if (r.code === 200) {
            returnField ? observer.next(r[returnField]) : observer.next();
            observer.complete();
          } else {
            observer.error(r.message);
          }
        },
        e => observer.error(e.message)
      )
    );
  }
}
